/**
 * 卡牌的**通用形状**。
 *
 * ⚠️ 这里是**唯一**决定卡牌长宽比的地方。
 * 每种牌自己的画法不许改比例 —— 它只会被告知「画到 W×H 里」，
 * 而 W:H 永远由这个文件给出。
 *
 * 这样做的理由很实际：牌面画法将来会越来越多（56 张各画各的，
 * 还要接 shader），如果每张牌自己决定比例，手牌一排起来就会高矮不齐，
 * 而且改比例要改 56 处。把形状收在通用层，画法只管内容。
 */

/** 标准扑克牌比例（2.5 : 3.5 ≈ 0.714）。Balatro 用的也是这个 */
export const CARD_ASPECT = 2.5 / 3.5;

/** 逻辑画布上一张牌的基准高度。宽度由比例推出 */
export const CARD_HEIGHT = 140;

/** 由高度推出宽度。高是主参数，因为一排手牌要对齐的是高度 */
export const CARD_WIDTH = CARD_HEIGHT * CARD_ASPECT;

/** 一个矩形的宽高（逻辑像素） */
export interface FaceSize {
  readonly width: number;
  readonly height: number;
}

/**
 * 按通用比例算出牌面尺寸。
 *
 * 传高度、得宽高 —— 因为"牌多高"才是布局真正要控制的量，
 * 宽度跟着比例走。
 */
export function faceSize(height: number = CARD_HEIGHT): FaceSize {
  return { width: height * CARD_ASPECT, height };
}

/** 尺寸是不是通用比例 —— 给自己加的护栏，牌面缓存用它挡住"手填尺寸"的误用 */
export function hasCardAspect(size: FaceSize): boolean {
  if (size.height <= 0) return false;
  return Math.abs(size.width / size.height - CARD_ASPECT) < 1e-6;
}

// ============================================================
// 出血内边距
// ============================================================

/**
 * 牌面的**出血内边距**，相对牌宽。
 *
 * 破框（BOLD 三规则之一）要求主图形可以伸出牌外 —— 伸出去的那部分必须
 * **真的被画出来**，所以 FBO 要比牌本身大一圈。0.25 是设计规则里的**上限**
 * （白卡用满，单据只用 0.10），FBO 一律按上限开，各原型自己决定用多少。
 *
 * ⚠️ 为什么是一个全局值而不是每张牌一个：纹理尺寸是**缓存键的一部分**，
 * 每张牌不同的话，同一张牌在不同尺寸下还要再分桶。而多开的那点显存
 * 换不来任何东西 —— 出血区是透明的，不画就是空的。
 */
export const FACE_PAD_RATIO = 0.25;

/** 出血内边距的实际宽度（逻辑像素） */
export function facePad(size: FaceSize = faceSize()): number {
  return size.width * FACE_PAD_RATIO;
}

/** 纹理尺寸 = 牌面尺寸 + 两侧出血 */
export interface PaddedFaceSize extends FaceSize {
  readonly pad: number;
}

export function paddedFaceSize(size: FaceSize = faceSize()): PaddedFaceSize {
  const pad = facePad(size);
  return { width: size.width + pad * 2, height: size.height + pad * 2, pad };
}

/**
 * 从纹理宽度反推出血内边距。
 *
 * ⚠️ 牌面纹理**含出血**，所以 `texture.width ≠ 牌的宽度`。
 * `CardView` 用它把两件事分开：网格铺的是含出血的那块，
 * 而挖洞 / 命中测试要的是**牌本身**。
 */
export function padOfTexture(textureWidth: number, size: FaceSize): number {
  return (textureWidth - size.width) / 2;
}
